import React, { forwardRef, useMemo, useCallback } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { CardBlockProps, Dossier, ContentItem, DisplayItem as DisplayItemType } from '../types';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface CardTimelineProps extends CardBlockProps { }

interface ParsedDate {
    month: string;
    day: string;
    year: string;
}

export const CardTimeline = forwardRef<HTMLDivElement, CardTimelineProps>(
    ({ kedo, block, data, listItems, getValue }, ref) => {
        const templateOptions = block.settings?.templateOptions;

        const fieldConfig = useMemo(() => {
            if (!templateOptions) return null;

            return {
                date: listItems.find((item) => item.id === templateOptions.date),
                title: listItems.find((item) => item.id === templateOptions.title),
                subtitle: listItems.find((item) => item.id === templateOptions.subtitle),
                description: listItems.find((item) => item.id === templateOptions.description),
            };
        }, [templateOptions, listItems]);

        const getDataValue = useCallback(
            (field: DisplayItemType | undefined, dossierData: ContentItem[]): string => {
                if (!field || !dossierData) return '';

                const singleDossierData = dossierData.find(
                    (dataItem) => dataItem.content.id === field.id
                );

                if (!singleDossierData?.content?.content) {
                    return '';
                }

                return singleDossierData.content.content;
            },
            []
        );

        // Parse date into month, day, year components
        const getParsedDate = useCallback(
            (dossierData: ContentItem[]): ParsedDate => {
                if (!fieldConfig?.date) return { month: '', day: '', year: '' };
                const rawValue = getDataValue(fieldConfig.date, dossierData);

                if (!rawValue) return { month: '', day: '', year: '' };

                const date = new Date(rawValue);
                const months = [
                    kedo.t('Jan'),
                    kedo.t('Feb'),
                    kedo.t('Mar'),
                    kedo.t('Apr'),
                    kedo.t('May'),
                    kedo.t('Jun'),
                    kedo.t('Jul'),
                    kedo.t('Aug'),
                    kedo.t('Sept'),
                    kedo.t('Oct'),
                    kedo.t('Nov'),
                    kedo.t('Dec'),
                ];

                return {
                    month: months[date.getMonth()] || '',
                    day: String(date.getDate()).padStart(2, '0'),
                    year: String(date.getFullYear()),
                };
            },
            [fieldConfig?.date, getDataValue, kedo]
        );

        if (!templateOptions || !fieldConfig) {
            return null;
        }

        return (
            <Box ref={ref} sx={{ width: '100%', py: 4 }}>
                <Box
                    sx={{
                        position: 'relative',
                        maxWidth: 900,
                        mx: 'auto',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            top: 0,
                            bottom: 0,
                            width: '2px',
                            bgcolor: '#e0e0e0',
                        },
                    }}
                >
                    {data.dossiers.map((dossier, index) => {
                        const dossierData = data.contents.filter(
                            (item) => item.id === dossier.id
                        );
                        const isLeft = index % 2 === 0;
                        const parsedDate = getParsedDate(dossierData);

                        return (
                            <Box
                                key={dossier.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 4,
                                    position: 'relative',
                                }}
                            >

                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        bgcolor: '#1976d2',
                                        zIndex: 1,
                                        boxShadow: '0 0 0 4px rgba(25, 118, 210, 0.15)',
                                    }}
                                />

                                {
                                    !isLeft && <>
                                        <Box sx={{ width: '45%' }} />
                                        <Box sx={{ width: '2%' }} />
                                    </>
                                }
                                <Box
                                    sx={{
                                        width: '50%',
                                        display: 'flex',
                                        justifyContent: isLeft ? 'flex-end' : 'flex-start',
                                        pr: isLeft ? 4 : 0,
                                        pl: isLeft ? 0 : 4,

                                    }}
                                >
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: isLeft ? 'row' : 'row-reverse',
                                            alignItems: 'stretch',
                                            bgcolor: '#fff',
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            maxWidth: 380,
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                            border: '1px solid #f0f0f0',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                                                transform: 'translateY(-2px)',
                                            },
                                        }}
                                        onClick={() => {
                                            window.location.href = `/contentdossier/${dossier.id}`;
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                flex: 1,
                                                p: 2,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                component="h3"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 0.5,
                                                    color: '#1a1a1a',
                                                    fontSize: '0.95rem',
                                                    lineHeight: 1.3,
                                                }}
                                            >
                                                {fieldConfig.title
                                                    ? getValue(fieldConfig.title, dossierData, dossier)
                                                    : dossier.summary}
                                            </Typography>
                                            {fieldConfig.subtitle && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#666',
                                                        mb: 0.5,
                                                        fontSize: '0.8rem',
                                                    }}
                                                >
                                                    {getValue(fieldConfig.subtitle, dossierData, dossier)}
                                                </Typography>
                                            )}
                                            {fieldConfig.description && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#888',
                                                        fontSize: '0.8rem',
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {getValue(fieldConfig.description, dossierData, dossier)}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minWidth: 70,
                                                px: 2,
                                                py: 1.5,
                                                borderLeft: '1px solid #f0f0f0',
                                                bgcolor: '#fafafa',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: '#666',
                                                    fontWeight: 500,
                                                    fontSize: '0.75rem',
                                                    textTransform: 'capitalize',
                                                }}
                                            >
                                                {parsedDate.month}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    color: '#1a1a1a',
                                                    fontSize: '1.75rem',
                                                    lineHeight: 1,
                                                    my: 0.25,
                                                }}
                                            >
                                                {parsedDate.day}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: '#888',
                                                    fontSize: '0.75rem',
                                                }}
                                            >
                                                {parsedDate.year}
                                            </Typography>
                                        </Box>
                                    </Paper>

                                </Box>
                                {
                                    isLeft && <>
                                        <Box sx={{ width: '2%' }} />
                                        <Box sx={{ width: '45%' }} />
                                    </>
                                }


                            </Box>
                        );
                    })}
                </Box>
            </Box>
        );
    }
);

CardTimeline.displayName = 'CardTimeline';
